import { KINDS } from "./labels";
import { clearPersistedJob } from "../ws/JobPersistenceService";

/**
 * Dismiss a single card by kind + jobId.
 * On multi-job slices ({ jobs: {} }) the selector always hands back the key the
 * reducer filed the job under, so the dismiss targets exactly one job — never
 * the "all" payload, which would also delete warning/error jobs that must be
 * acknowledged per-card. Only a legacy flat slice (one job, no map) reaches the
 * whole-slice dismiss.
 * Also clears the job from localStorage persistence.
 */
export const dismissCard = (kind, jobId = null) => (dispatch) => {
	const entry = KINDS.find((k) => k.kind === kind);
	if (!entry) return;
	if (jobId) {
		// Remove from persistence so it doesn't resurface on next refresh.
		clearPersistedJob(jobId);
		dispatch({ type: entry.dismissType, payload: { jobId } });
		return;
	}
	dispatch({ type: entry.dismissType, payload: { jobId: "all" } });
};

/**
 * "Clear completed" header action: dismiss fully-successful terminated jobs
 * across all slices. Deliberately leaves warning (partial failure) and error
 * cards in place — they are the only record of what failed and must be
 * acknowledged per-card. Works with both old flat slices and new
 * { jobs: {} } slices.
 */
export const clearCompleted = () => (dispatch, getState) => {
	const state = getState();
	for (const { stateKey, dismissType } of KINDS) {
		const slice = state[stateKey];
		if (!slice) continue;

		// New multi-job shape. Dismiss each done job by its concrete jobId —
		// the reducers' "all" branch would also delete error/warning jobs
		// sharing the slice.
		if (slice.jobs) {
			for (const [jobId, job] of Object.entries(slice.jobs)) {
				const isDone =
					!job.active &&
					job.terminatedAt &&
					!job.error &&
					(!job.failedCount || job.failedCount === 0);
				if (!isDone) continue;
				if (job.jobId) clearPersistedJob(job.jobId);
				dispatch({ type: dismissType, payload: { jobId: job.jobId ?? jobId } });
			}
			continue;
		}

		// Legacy flat shape
		const isDone =
			!slice.active &&
			slice.terminatedAt &&
			!slice.error &&
			(!slice.failedCount || slice.failedCount === 0);
		if (isDone) dispatch({ type: dismissType });
	}
};
